import { SOCIAL_IMAGE_INPUT_LIMIT, SOCIAL_IMAGE_OUTPUT_LIMIT } from './types';

export async function optimizeSocialImage(file: File): Promise<File> {
  if (!['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/gif'].includes(file.type)) throw new Error('Choose a JPEG, PNG, WebP, AVIF or GIF image.');
  if (file.size > SOCIAL_IMAGE_INPUT_LIMIT || !file.size) throw new Error('Choose an image smaller than 10 MB.');
  // Canvas compression would flatten GIF animation. The server validates all
  // frames and converts the original GIF to bounded animated WebP.
  if (file.type === 'image/gif') return file;
  const bitmap = await createImageBitmap(file);
  try {
    if (bitmap.width * bitmap.height > 40_000_000) throw new Error('Choose an image under 40 megapixels.');
    const scale = Math.min(1, 1920 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Image optimization is unavailable in this browser.');
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.82, 0.72, 0.60, 0.48]) {
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/webp', quality));
      if (!blob || blob.type !== 'image/webp') throw new Error('This browser cannot prepare this image. Try a current browser.');
      if (blob.size <= SOCIAL_IMAGE_OUTPUT_LIMIT) return new File([blob], 'photo.webp', { type: 'image/webp' });
    }
    throw new Error('This image is too detailed. Choose a smaller image.');
  } finally { bitmap.close(); }
}
